import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, Observable, ReplaySubject } from 'rxjs';
import { AssignAircraftConfirmationModalComponent } from 'src/app/components/assign-aircraft-confirmation-modal/assign-aircraft-confirmation-modal.component';
import { ButtonComponent } from 'src/app/components/button/button.component';
import { FileUploadComponent } from 'src/app/components/file-upload/file-upload.component';
import {
  ColumnDefinition,
  DataColumnDefinition,
} from 'src/app/components/qs-table/column-definition.interface';
import { QsTableComponent } from 'src/app/components/qs-table/qs-table.component';
import {
  Aircraft,
  AircraftsRegistrationPlan,
  AircraftsRegistrationResponse,
  ReasonEnum,
} from 'src/app/models/aircraft-registration.model';
import { CompanyNameType } from 'src/app/models/company.model';
import { AircraftRegistrationService } from 'src/app/services/aircraft-registration.service';
import { AlertService } from 'src/app/services/alert.service';
import { LoadingService } from 'src/app/services/loading.service';

type Step = 'upload' | 'review' | 'approve';
@Component({
  selector: 'app-add-aircraft',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    FileUploadComponent,
    MatCardModule,
    TranslateModule,
    MatSelectModule,
    ButtonComponent,
    QsTableComponent,
    MatDialogModule,
    MatTooltipModule,
  ],
  templateUrl: './aircraft-registration.component.html',
  styleUrl: './aircraft-registration.component.scss',
})
export class AircraftRegistrationComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private loadingService = inject(LoadingService);
  private aircraftRegistrationService = inject(AircraftRegistrationService);
  private dialog = inject(MatDialog);
  private alert = inject(AlertService);
  private translateService = inject(TranslateService);

  aircraftForm!: FormGroup;
  selectedCompany: CompanyNameType | undefined;
  uploadedCSV: File | undefined;

  aircraftFromCSV: Aircraft[] = [];

  validatedAircraft: AircraftsRegistrationResponse[] = [];
  hasValidAsignableAircraft = false;

  steps = {
    upload: true,
    review: false,
    approve: false,
  };

  companies: CompanyNameType[] = this.activatedRoute.snapshot.data['companies'];

  reviewTableColumn: ColumnDefinition<Aircraft>[] = [
    {
      type: 'data',
      field: 'name',
      header: this.translateService.instant('aircraft.registration.name'),
    } as DataColumnDefinition<Aircraft>,
    {
      type: 'data',
      field: 'licenseKey',
      header: this.translateService.instant('aircraft.registration.licenseKey'),
    } as DataColumnDefinition<Aircraft>,
    {
      type: 'data',
      field: 'description',
      header: this.translateService.instant(
        'aircraft.registration.description',
      ),
    } as DataColumnDefinition<Aircraft>,
    {
      type: 'data',
      field: 'unitNumber',
      header: this.translateService.instant('aircraft.registration.unitNumber'),
    } as DataColumnDefinition<Aircraft>,
    {
      type: 'data',
      field: 'modelKey',
      header: this.translateService.instant('aircraft.registration.modelKey'),
    } as DataColumnDefinition<Aircraft>,
    {
      type: 'data',
      field: 'reason',
      header: this.translateService.instant(
        'aircraft.registration.validationRemark',
      ),
      format: (row) => {
        return '';
      },
    } as DataColumnDefinition<Aircraft>,
  ];
  validationTableColumn: ColumnDefinition<AircraftsRegistrationResponse>[] = [
    {
      type: 'data',
      field: 'name',
      header: this.translateService.instant('aircraft.registration.name'),
      format: (row: AircraftsRegistrationResponse) => row.aircraft.name,
      sortFn: (
        a: AircraftsRegistrationResponse,
        b: AircraftsRegistrationResponse,
        multiplier,
      ) => {
        const valueA = a.aircraft.name!;
        const valueB = b.aircraft.name!;
        let comparator = 0;
        if (valueA < valueB) {
          comparator = -1;
        } else if (valueA > valueB) {
          comparator = 1;
        }
        return comparator * multiplier;
      },
    } as DataColumnDefinition<AircraftsRegistrationResponse>,
    {
      type: 'data',
      field: 'licenseKey',
      header: this.translateService.instant('aircraft.registration.licenseKey'),
      format: (row: AircraftsRegistrationResponse) => row.aircraft.licenseKey,
      sortFn: (
        a: AircraftsRegistrationResponse,
        b: AircraftsRegistrationResponse,
        multiplier,
      ) => {
        const valueA = a.aircraft.licenseKey!;
        const valueB = b.aircraft.licenseKey!;
        let comparator = 0;
        if (valueA < valueB) {
          comparator = -1;
        } else if (valueA > valueB) {
          comparator = 1;
        }
        return comparator * multiplier;
      },
    } as DataColumnDefinition<AircraftsRegistrationResponse>,
    {
      type: 'data',
      field: 'description',
      header: this.translateService.instant(
        'aircraft.registration.description',
      ),
      format: (row: AircraftsRegistrationResponse) => row.aircraft.description,
      sortFn: (
        a: AircraftsRegistrationResponse,
        b: AircraftsRegistrationResponse,
        multiplier,
      ) => {
        const valueA = a.aircraft.description!;
        const valueB = b.aircraft.description!;
        let comparator = 0;
        if (valueA < valueB) {
          comparator = -1;
        } else if (valueA > valueB) {
          comparator = 1;
        }
        return comparator * multiplier;
      },
    } as DataColumnDefinition<AircraftsRegistrationResponse>,
    {
      type: 'data',
      field: 'unitNumber',
      header: this.translateService.instant('aircraft.registration.unitNumber'),
      format: (row: AircraftsRegistrationResponse) => row.aircraft.unitNumber,
      sortFn: (
        a: AircraftsRegistrationResponse,
        b: AircraftsRegistrationResponse,
        multiplier,
      ) => {
        const valueA = a.aircraft.unitNumber!;
        const valueB = b.aircraft.unitNumber!;
        let comparator = 0;
        if (valueA < valueB) {
          comparator = -1;
        } else if (valueA > valueB) {
          comparator = 1;
        }
        return comparator * multiplier;
      },
    } as DataColumnDefinition<AircraftsRegistrationResponse>,
    {
      type: 'data',
      field: 'modelKey',
      header: this.translateService.instant('aircraft.registration.modelKey'),
      format: (row: AircraftsRegistrationResponse) => row.aircraft.modelKey,
      sortFn: (
        a: AircraftsRegistrationResponse,
        b: AircraftsRegistrationResponse,
        multiplier,
      ) => {
        const valueA = a.aircraft.modelKey!;
        const valueB = b.aircraft.modelKey!;
        let comparator = 0;
        if (valueA < valueB) {
          comparator = -1;
        } else if (valueA > valueB) {
          comparator = 1;
        }
        return comparator * multiplier;
      },
    } as DataColumnDefinition<AircraftsRegistrationResponse>,
    {
      type: 'data',
      field: 'reason',
      header: this.translateService.instant(
        'aircraft.registration.validationRemark',
      ),
      format: (row: AircraftsRegistrationResponse) => {
        if (row.reasonDetails?.companyId)
          return (
            row.reason
              .replace(/([A-Z])/g, ' $1')
              .toLowerCase()
              .replace(/^./, (c) => c.toUpperCase()) +
            ' (' +
            this.getCompanyNameById(row.reasonDetails?.companyId!) +
            ')'
          );
        return row.reason
          .replace(/([A-Z])/g, ' $1')
          .toLowerCase()
          .replace(/^./, (c) => c.toUpperCase());
      },
    } as DataColumnDefinition<AircraftsRegistrationResponse>,
  ];
  getCompanyNameById(id: string): string {
    return this.companies.filter((x) => x.id === id)[0]?.name;
  }

  ngOnInit() {}

  changeStep(step: Step): void {
    switch (step) {
      case 'upload':
        this.steps.review = false;
        this.steps.approve = false;
        this.steps.upload = true;
        this.aircraftFromCSV = [];
        this.uploadedCSV = undefined;
        this.hasValidAsignableAircraft = false;
        break;

      case 'review':
        this.steps.review = true;
        this.steps.approve = false;
        this.steps.upload = false;
        this.hasValidAsignableAircraft = false;
        break;

      case 'approve':
        this.steps.review = false;
        this.steps.approve = true;
        this.steps.upload = false;

        break;

      default:
        this.steps.review = false;
        this.steps.approve = false;
        this.steps.upload = true;
        this.hasValidAsignableAircraft = false;
        break;
    }
  }

  async parseCSV(file: File): Promise<Aircraft[]> {
    const text = await file.text();
    const lines = text
      .trim()
      .split('\n')
      .map((line) => line.split(','));
    const headers = lines[0].map((header) => header.trim());
    const data: Aircraft[] = [];

    for (let i = 1; i < lines.length; i++) {
      const row = lines[i];
      const obj: any = {};

      for (let j = 0; j < headers.length; j++) {
        const key = headers[j];
        const value = row[j]?.trim();
        switch (key) {
          case 'unitNumber':
          case 'modelKey':
          case 'name':
            obj[key] = value ?? '';
            break;
          case 'licenseKey':
            obj[key] = value ?? '';
            break;
          case 'description':
            obj[key] = value || undefined;
            break;
          default:
            obj[key] = value;
        }
      }

      data.push(obj as Aircraft);
    }

    return data;
  }

  getAircraftRowClass(row: AircraftsRegistrationResponse): {
    [key: string]: string;
  } {
    let obj = { 'background-color': 'unset' };
    if (row.reason !== ReasonEnum.Valid) {
      obj = { 'background-color': '#d3c069' };
    }
    return obj;
  }

  async onFileSelected(file: File) {
    this.loadingService.loading();
    if (file) {
      this.uploadedCSV = file;
      try {
        const parsedData = await this.parseCSV(file);
        this.aircraftFromCSV = parsedData;
        this.loadingService.loaded();
        this.changeStep('review');
        this.alert.success(
          this.translateService.instant(
            'aircraft.registration.csvParseSuccess',
          ),
        );
      } catch (error) {
        this.loadingService.loaded();
        this.alert.error(
          this.translateService.instant('aircraft.registration.csvParseError'),
        );
        console.error('Error parsing CSV:', error);
      }
    }
  }
  removeAttachment(): void {
    this.changeStep('upload');
  }

  onValidationRequest(): void {
    this.loadingService.loading();
    const plan: AircraftsRegistrationPlan = {
      companyId: this.selectedCompany?.id!,
      aircrafts: this.aircraftFromCSV,
      dryRun: true,
    };

    this.aircraftRegistrationService
      .applyAircraftsRegistration(plan)
      .pipe(finalize(() => this.loadingService.loaded()))
      .subscribe({
        next: (response: AircraftsRegistrationResponse[]) => {
          this.validatedAircraft = response;
          this.hasValidAsignableAircraft =
            this.validatedAircraft.filter(
              (el) => el.reason === ReasonEnum.Valid,
            ).length > 0;
          this.changeStep('approve');
        },
        error: (err) => {
          this.alert.error(
            this.translateService.instant('aircraft.registration.error'),
          );
          console.error('Validation error', err);
        },
      });
  }
  openAssignAircraftModal() {
    const dialogRef = this.dialog.open(
      AssignAircraftConfirmationModalComponent,
      {
        width: '500px',
        data: {
          validCount: this.validatedAircraft.filter(
            (x) => x.reason === ReasonEnum.Valid,
          ).length,
          invalidCount: this.validatedAircraft.filter(
            (x) => x.reason !== ReasonEnum.Valid,
          ).length,
          companyName:
            this.selectedCompany?.name ||
            this.translateService.instant(
              'aircraft.registration.selectedCompanyFallback',
            ),
        },
      },
    );

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        const validAircrafts = this.validatedAircraft
          .filter((x) => x.reason === ReasonEnum.Valid)
          .map((x) => x.aircraft);
        this.applyAircraftsRegistration(
          this.selectedCompany?.id!,
          validAircrafts,
        );
      }
    });
  }

  applyAircraftsRegistration(
    companyId: string,
    aircrafts: Aircraft[],
  ): Observable<void> {
    this.loadingService.loading();
    const replySubject: ReplaySubject<void> = new ReplaySubject<void>();
    this.aircraftRegistrationService
      .applyAircraftsRegistration({ companyId, aircrafts, dryRun: false })
      .pipe(
        finalize(() => {
          replySubject.next();
          replySubject.complete();
          this.loadingService.loaded();
        }),
      )
      .subscribe({
        complete: () => {
          this.removeAttachment();
          this.selectedCompany = undefined;
          this.alert.success(
            this.translateService.instant('aircraft.registration.success'),
          );
        },
        error: (err) => {
          this.alert.error(
            this.translateService.instant('aircraft.registration.error'),
          );
          console.error(' error', err);
        },
      });

    return replySubject.asObservable();
  }
}
